import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './BiDashboard.css';
import './Reports.css';
import {
  HiOutlineChevronDown,
  HiOutlinePlus,
  HiOutlineViewBoards,
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiDotsHorizontal,
  HiOutlineTrash,
} from 'react-icons/hi';

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { plantService } from '../services';
import MetricCardLibrary from './MetricCardLibrary';
import SharedModal from '../components/common/SharedModal';

// Mock Data for Demo
const lineChartData = [
  { name: 'Jul 2020', value: 750 },
  { name: 'Aug 2020', value: 510 },
  { name: 'Sep 2020', value: 740 },
  { name: 'Oct 2020', value: 600 },
  { name: 'Nov 2020', value: 610 },
  { name: 'Dec 2020', value: 670 },
  { name: 'Jan 2021', value: 580 },
  { name: 'Feb 2021', value: 430 },
  { name: 'Mar 2021', value: 380 },
  { name: 'Apr 2021', value: 550 },
  { name: 'May 2021', value: 350 },
];

const lineChartDataWeek = [
  { name: 'Mon', value: 120 },
  { name: 'Tue', value: 132 },
  { name: 'Wed', value: 101 },
  { name: 'Thu', value: 134 },
  { name: 'Fri', value: 90 },
  { name: 'Sat', value: 230 },
  { name: 'Sun', value: 210 },
];

const lineChartDataYear = [
  { name: '2018', value: 4000 },
  { name: '2019', value: 3000 },
  { name: '2020', value: 5000 },
  { name: '2021', value: 2780 },
  { name: '2022', value: 1890 },
];

const laborBarData = [
  { name: 'Dept A', value: 80 },
  { name: 'Dept B', value: 98 },
  { name: 'Dept C', value: 98 },
  { name: 'Dept D', value: 80 },
  { name: 'Dept E', value: 50 },
];

const laborPieData = [
  { name: 'Group A', value: 400 },
  { name: 'Group B', value: 300 },
  { name: 'Group C', value: 300 },
  { name: 'Group D', value: 200 },
];

const stackedBarData = [
  { name: 'Qtr 1', scheduled: 23, nonScheduled: 21 },
  { name: 'Qtr 2', scheduled: 30, nonScheduled: 18 },
  { name: 'Qtr 3', scheduled: 32, nonScheduled: 18 },
  { name: 'Qtr 4', scheduled: 22, nonScheduled: 12 },
];

const stackedBarDataWeek = [
  { name: 'Mon', scheduled: 5, nonScheduled: 3 },
  { name: 'Tue', scheduled: 7, nonScheduled: 4 },
  { name: 'Wed', scheduled: 6, nonScheduled: 2 },
  { name: 'Thu', scheduled: 8, nonScheduled: 5 },
  { name: 'Fri', scheduled: 9, nonScheduled: 4 },
  { name: 'Sat', scheduled: 4, nonScheduled: 6 },
  { name: 'Sun', scheduled: 3, nonScheduled: 2 },
];

const stackedBarDataYear = [
  { name: '2019', scheduled: 120, nonScheduled: 80 },
  { name: '2020', scheduled: 150, nonScheduled: 90 },
  { name: '2021', scheduled: 180, nonScheduled: 100 },
  { name: '2022', scheduled: 200, nonScheduled: 120 },
];

const denseBarData = Array.from({ length: 30 }, (_, i) => {
  const scheduled = Math.floor(Math.random() * 20) + 60;
  return {
    name: (i + 1).toString(),
    scheduled: scheduled,
    nonScheduled: 100 - scheduled
  };
});

const denseBarDataWeek = Array.from({ length: 7 }, (_, i) => {
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const scheduled = Math.floor(Math.random() * 20) + 60;
  return {
    name: days[i],
    scheduled: scheduled,
    nonScheduled: 100 - scheduled
  };
});

const denseBarDataYear = Array.from({ length: 12 }, (_, i) => {
  const months = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
  const scheduled = Math.floor(Math.random() * 20) + 60;
  return {
    name: months[i],
    scheduled: scheduled,
    nonScheduled: 100 - scheduled
  };
});

const soCategoriesData = [
  { name: 'Electrical', value: 30 },
  { name: 'Mechanical', value: 45 },
  { name: 'Civil', value: 20 },
  { name: 'Running', value: 15 },
  { name: 'Preventive', value: 25 },
];

const soTypesData = [
  { name: 'Planned', value: 65 },
  { name: 'Unplanned', value: 35 },
  { name: 'Emergency', value: 10 },
  { name: 'Condition', value: 20 },
];

const soCategoriesDataWeek = [
  { name: 'Electrical', value: 5 },
  { name: 'Mechanical', value: 8 },
  { name: 'Civil', value: 3 },
  { name: 'Running', value: 2 },
  { name: 'Preventive', value: 4 },
];

const soCategoriesDataYear = [
  { name: 'Electrical', value: 300 },
  { name: 'Mechanical', value: 450 },
  { name: 'Civil', value: 200 },
  { name: 'Running', value: 150 },
  { name: 'Preventive', value: 250 },
];

const soTypesDataWeek = [
  { name: 'Planned', value: 12 },
  { name: 'Unplanned', value: 8 },
  { name: 'Emergency', value: 2 },
  { name: 'Condition', value: 5 },
];

const soTypesDataYear = [
  { name: 'Planned', value: 650 },
  { name: 'Unplanned', value: 350 },
  { name: 'Emergency', value: 100 },
  { name: 'Condition', value: 200 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

// Synchronous cache loading function for plants
const getCachedPlantsData = () => {
  try {
    const cachedPlants = sessionStorage.getItem('plants_data');
    if (cachedPlants) {
      return JSON.parse(cachedPlants);
    }
    return [];
  } catch (e) {
    console.error('Error parsing cached plants data:', e);
    return [];
  }
};

const BiDashboard = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('module');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const cachedPlants = getCachedPlantsData();
  const [plants, setPlants] = useState(cachedPlants);
  const [selectedPlant, setSelectedPlant] = useState('');

  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [dateRangeType, setDateRangeType] = useState('today'); 
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [customCards, setCustomCards] = useState([]);

  useEffect(() => {
    const fetchPlants = async () => {
      try {
        const data = await plantService.getAllPlants();
        setPlants(data || []);
        if (data?.length === 1) {
          setSelectedPlant(data[0].plantId.toString());
        }
        if (data) {
          sessionStorage.setItem('plants_data', JSON.stringify(data));
        }
      } catch (err) {
        console.error('Failed to fetch plants', err);
      }
    };
    fetchPlants();
  }, []);

  const loadCards = () => {
    const savedCards = localStorage.getItem('dashboard_custom_cards');
    if (savedCards) {
      setCustomCards(JSON.parse(savedCards));
    }
  };

  useEffect(() => {
    loadCards();
  }, []);

  const [showMetricLibraryModal, setShowMetricLibraryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);

  const [soTrendTimeFrame, setSoTrendTimeFrame] = useState('month');
  const [soBar1TimeFrame, setSoBar1TimeFrame] = useState('month');
  const [soBar2TimeFrame, setSoBar2TimeFrame] = useState('month');
  const [soDenseTimeFrame, setSoDenseTimeFrame] = useState('month');
  const [soCategoriesTimeFrame, setSoCategoriesTimeFrame] = useState('month');
  const [soTypesTimeFrame, setSoTypesTimeFrame] = useState('month');
  const [dashboardView, setDashboardView] = useState('bi');

  const oeeTrendData = [
    { name: 'Nov 15', availability: 85, performance: 90, quality: 98, oee: 75.2 },
    { name: 'Nov 16', availability: 82, performance: 88, quality: 97, oee: 77.8 },
    { name: 'Nov 17', availability: 88, performance: 92, quality: 99, oee: 79.5 },
    { name: 'Nov 18', availability: 84, performance: 89, quality: 98, oee: 78.2 },
    { name: 'Nov 19', availability: 86, performance: 91, quality: 98, oee: 80.1 },
    { name: 'Nov 20', availability: 87, performance: 93, quality: 99, oee: 81.5 },
    { name: 'Nov 21', availability: 85, performance: 90, quality: 98, oee: 78.5 },
  ];

  const themeColors = {
    grid: isDarkMode ? '#374151' : '#e5e7eb',
    text: isDarkMode ? '#9CA3AF' : '#6B7280',
    tooltipBg: isDarkMode ? '#111827' : '#FFFFFF',
    tooltipText: isDarkMode ? '#F3F4F6' : '#111827',
  };

  const handlePlantChange = (val) => {
    setSelectedPlant(val);
    if (val) {
      localStorage.setItem('defaultPlantId', val);
    }
  };

  const handleDateRangeChange = (type) => {
    setDateRangeType(type);
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    switch (type) {
      case 'today': break;
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        end.setDate(today.getDate() - 1);
        break;
      case 'mtd':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'ytd':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      case 'custom':
        setFromDate('');
        setToDate('');
        return;
      default: return;
    }

    setFromDate(formatDate(start));
    setToDate(formatDate(end));
  };

  useEffect(() => {
    handleDateRangeChange('today');
  }, []);

  const initiateDelete = (card) => {
    setCardToDelete(card);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (cardToDelete) {
      const updatedCards = customCards.filter(c => c.id !== cardToDelete.id);
      setCustomCards(updatedCards);
      localStorage.setItem('dashboard_custom_cards', JSON.stringify(updatedCards));
      setShowDeleteModal(false);
      setCardToDelete(null);
    }
  };

  const renderCustomChart = (card) => {
    const { type, data, timeFrame } = card;
    let chartData = data;
    if (timeFrame === 'last1year') {
      if (type === 'line' || type === 'bar') chartData = lineChartData;
    } else if (timeFrame === 'last2years') {
      if (type === 'line' || type === 'bar') chartData = lineChartDataYear;
    }

    switch(type) {
      case '123':
        return (
          <div className="bi-kpi-display">
            <span className="bi-kpi-value">406</span>
            <span className="bi-kpi-label">Total Hours</span>
          </div>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={themeColors.grid} strokeDasharray="3 3" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: themeColors.text }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: themeColors.text }} />
              <Tooltip 
                contentStyle={{ backgroundColor: themeColors.tooltipBg, border: '1px solid ' + themeColors.grid, color: themeColors.tooltipText }}
                itemStyle={{ color: themeColors.tooltipText }}
              />
              <Line type="monotone" dataKey="value" stroke="#0e5c8e" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: themeColors.tooltipBg, border: '1px solid ' + themeColors.grid, color: themeColors.tooltipText }}
                itemStyle={{ color: themeColors.tooltipText }}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      case 'table':
        return (
          <div className="bi-mini-table-container custom-scrollbar">
            <table className="table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index}>
                    <td>{row.name}</td>
                    <td className="font-bold text-primary">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'bar':
      default:
        return (
          <ResponsiveContainer width="100%" height="100%">
             <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
               <CartesianGrid vertical={false} stroke={themeColors.grid} strokeDasharray="3 3" />
               <XAxis dataKey="name" tick={{ fontSize: 12, fill: themeColors.text }} axisLine={false} tickLine={false} />
               <YAxis tick={{ fontSize: 12, fill: themeColors.text }} axisLine={false} tickLine={false} />
               <Tooltip 
                  contentStyle={{ backgroundColor: themeColors.tooltipBg, border: '1px solid ' + themeColors.grid, color: themeColors.tooltipText }}
                  itemStyle={{ color: themeColors.tooltipText }}
               />
               <Bar dataKey="value" fill="#0E7490" name="Value" radius={[4, 4, 0, 0]} barSize={40} />
             </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  const renderOeeDashboard = () => (
    <div className={`bi-grid ${viewMode}-view animate-fade-in`}>
      <div className="bi-card p-6 mb-6">
        <div className="bi-oee-top">
          <div className="bi-oee-overall">
            <span className="bi-oee-overall-label">OVERALL OEE</span>
            <span className="bi-oee-overall-value">78.5%</span>
            <button type="button" className="bi-oee-attention-btn">
              <span className="mr-2">⚡</span> Needs Attention
            </button>
          </div>
          <div className="bi-oee-bars">
            <div className="bi-metric-row">
              <div className="bi-metric-row-head">
                <span className="bi-metric-label">🕒 Availability</span>
                <span className="bi-metric-value">85.2%</span>
              </div>
              <div className="bi-progress-track">
                <div className="bi-progress-fill bi-progress-fill--info" style={{ width: '85.2%' }} />
              </div>
            </div>
            <div className="bi-metric-row">
              <div className="bi-metric-row-head">
                <span className="bi-metric-label">⚙️ Performance</span>
                <span className="bi-metric-value">92.1%</span>
              </div>
              <div className="bi-progress-track">
                <div className="bi-progress-fill bi-progress-fill--brand" style={{ width: '92.1%' }} />
              </div>
            </div>
            <div className="bi-metric-row">
              <div className="bi-metric-row-head">
                <span className="bi-metric-label">✨ Quality</span>
                <span className="bi-metric-value">99.8%</span>
              </div>
              <div className="bi-progress-track">
                <div className="bi-progress-fill bi-progress-fill--violet" style={{ width: '99.8%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bi-kpi-grid">
        {[
          { icon: '🕒', label: 'Production Hours', value: '720', sub: 'of 840 planned', type: 'info' },
          { icon: '⚙️', label: 'Cycle Time', value: '48.8s', sub: 'Ideal: 45s', type: 'info' },
          { icon: '✅', label: 'Good Units', value: '14,520', sub: 'of 14,550 total', type: 'success' },
          { icon: '📊', label: 'Target OEE', value: '85%', sub: '↓ 6.5% below', type: 'warn' }
        ].map((kpi, i) => (
          <div className="bi-card p-4 bi-kpi-card-row" key={i}>
            <div className={`bi-kpi-icon bi-kpi-icon--${kpi.type}`}>{kpi.icon}</div>
            <div>
               <div className="bi-kpi-stat-label">{kpi.label}</div>
               <div className="bi-kpi-stat-value">{kpi.value}</div>
               <div className={`bi-kpi-stat-sub ${kpi.type === 'warn' ? 'bi-kpi-stat-warn' : ''}`}>{kpi.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bi-card p-6">
        <h3 className="bi-card-title mb-6">7-Day OEE Trend</h3>
        <div className="bi-chart-area">
          <ResponsiveContainer width="100%" height="250px">
            <BarChart data={oeeTrendData} barGap={8}>
              <CartesianGrid vertical={false} stroke={themeColors.grid} strokeDasharray="3 3" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: themeColors.text }} />
              <YAxis hide />
              <Tooltip 
                cursor={{ fill: 'transparent' }}
                contentStyle={{ backgroundColor: themeColors.tooltipBg, border: '1px solid ' + themeColors.grid }}
              />
              <Bar dataKey="availability" fill="var(--status-info-text)" name="Availability" radius={[4, 4, 0, 0]} />
              <Bar dataKey="performance" fill="var(--accent-primary)" name="Performance" radius={[4, 4, 0, 0]} />
              <Bar dataKey="quality" fill="#8B5CF6" name="Quality" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`bi-dashboard-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <header className="bi-header">
        <div className="bi-header-left">
          <div className="flex flex-col">
            <h1 className="bi-header-title">BI Dashboard</h1>
          </div>
        </div>
        
        <div className="bi-header-right">
          <button className="btn-add-metric" onClick={() => setShowMetricLibraryModal(true)}>
            <HiOutlinePlus size={16} /> Add Metric Card
          </button>
          
          <div className="view-switch-group">
            <button className={`btn-view-toggle ${dashboardView === 'bi' ? 'active' : ''}`} onClick={() => setDashboardView('bi')}>Custom</button>
            <button className={`btn-view-toggle ${dashboardView === 'so' ? 'active' : ''}`} onClick={() => setDashboardView('so')}>SO Card</button>
            <button className={`btn-view-toggle ${dashboardView === 'oee' ? 'active' : ''}`} onClick={() => setDashboardView('oee')}>OEE</button>
          </div>

          <div className="layout-switcher">
            <button 
              className={`layout-btn ${viewMode === 'module' ? 'active' : ''}`} 
              onClick={() => setViewMode('module')}
              title="Grid View"
            >
              <HiOutlineViewGrid size={20} />
            </button>
            <button 
              className={`layout-btn ${viewMode === 'stream' ? 'active' : ''}`} 
              onClick={() => setViewMode('stream')}
              title="List View"
            >
              <HiOutlineViewList size={20} />
            </button>
          </div>
        </div>
      </header>
      
        <div className="bi-filter-bar">
          <div className="filter-item">
            <span className="filter-label">DATE RANGE</span>
            <div className="date-range-group">
              <select 
                className="form-select preset-select !py-1.5 !min-h-[38px]" 
                value={dateRangeType} 
                onChange={(e) => handleDateRangeChange(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="mtd">MTD</option>
                <option value="ytd">YTD</option>
                <option value="custom">Custom Range</option>
              </select>
              <div className="date-inputs">
                <input 
                  type="date" 
                  className="form-input !py-1 !px-2 !min-h-[32px] !border-none !bg-transparent" 
                  value={fromDate} 
                  onChange={(e) => { setFromDate(e.target.value); setDateRangeType('custom'); }} 
                />
                <span className="date-sep">-</span>
                <input 
                  type="date" 
                  className="form-input !py-1 !px-2 !min-h-[32px] !border-none !bg-transparent" 
                  value={toDate} 
                  onChange={(e) => { setToDate(e.target.value); setDateRangeType('custom'); }} 
                />
              </div>
            </div>
          </div>

          <div className="filter-divider" />

          <div className="filter-item">
            <span className="filter-label">PLANT</span>
            <div className="plant-select-wrapper">
              <select 
                className="form-select plant-select !py-1.5 !min-h-[38px]" 
                value={selectedPlant} 
                onChange={(e) => handlePlantChange(e.target.value)}
              >
                <option value="">All Plants</option>
                {plants.map(p => (
                  <option key={p.plantId} value={p.plantId}>
                    {p.plantCode} - {p.plantName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

      <div className="bi-content-area">
        {dashboardView === 'bi' && (
          <div className={`bi-grid ${viewMode}-view`}>
            {customCards.map((card) => (
              <div className="bi-card" key={card.id}>
                <div className="bi-card-header">
                  <div>
                    <h3 className="bi-card-title">{card.title}</h3>
                    <p className="bi-card-subtitle">{card.category || 'Metric'}</p>
                  </div>
                  <div className="bi-card-actions">
                    <button className="btn-icon text-error" onClick={() => initiateDelete(card)}><HiOutlineTrash size={18} /></button>
                  </div>
                </div>
                <div className="bi-card-body">{renderCustomChart(card)}</div>
              </div>
            ))}
            <div className="bi-card bi-card-placeholder" onClick={() => setShowMetricLibraryModal(true)}>
              <div className="placeholder-icon"><HiOutlinePlus size={28} /></div>
              <div className="placeholder-text">
                <span className="font-bold">Add Metric Card</span>
                <p>Click to configure a new metric</p>
              </div>
            </div>
          </div>
        )}

        {dashboardView === 'so' && (
          <div className={`bi-grid ${viewMode}-view`}>
            {[
              { title: 'SO Trend Dolvi', data: soTrendTimeFrame === 'week' ? lineChartDataWeek : soTrendTimeFrame === 'year' ? lineChartDataYear : lineChartData, type: 'line', tf: soTrendTimeFrame, setTf: setSoTrendTimeFrame },
              { title: 'SO Scheduling', data: soBar1TimeFrame === 'week' ? stackedBarDataWeek : soBar1TimeFrame === 'year' ? stackedBarDataYear : stackedBarData, type: 'stackedBar', tf: soBar1TimeFrame, setTf: setSoBar1TimeFrame },
              { title: 'SO Categories', data: soCategoriesTimeFrame === 'week' ? soCategoriesDataWeek : soCategoriesTimeFrame === 'year' ? soCategoriesDataYear : soCategoriesData, type: 'bar', tf: soCategoriesTimeFrame, setTf: setSoCategoriesTimeFrame }
            ].map((chart, i) => (
              <div className="bi-card" key={i}>
                <div className="bi-card-header">
                  <h3 className="bi-card-title">{chart.title}</h3>
                  <select className="form-select max-w-[120px] !py-1 !px-2 !min-h-[32px] !text-[0.75rem]" value={chart.tf} onChange={(e) => chart.setTf(e.target.value)}>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="year">Year</option>
                  </select>
                </div>
                <div className="bi-card-body">
                  <ResponsiveContainer width="100%" height="100%">
                    {chart.type === 'line' ? (
                      <LineChart data={chart.data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={themeColors.grid} />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: themeColors.text }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: themeColors.text }} />
                        <Tooltip contentStyle={{ backgroundColor: themeColors.tooltipBg, border: 'none', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                        <Line type="linear" dataKey="value" stroke="var(--accent-primary)" strokeWidth={2} dot={{ r: 4, fill: '#fff', stroke: 'var(--accent-primary)', strokeWidth: 2 }} />
                      </LineChart>
                    ) : (
                      <BarChart data={chart.data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke={themeColors.grid} />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: themeColors.text }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: themeColors.text }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="var(--accent-primary)" radius={[4, 4, 0, 0]} />
                        {chart.type === 'stackedBar' && <Bar dataKey="scheduled" stackId="a" fill="var(--status-error)" />}
                        {chart.type === 'stackedBar' && <Bar dataKey="nonScheduled" stackId="a" fill="var(--accent-primary)" />}
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>
        )}
        {dashboardView === 'oee' && renderOeeDashboard()}
      </div>

      <SharedModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Metric Card"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <button className="btn-secondary btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
            <button className="btn-danger btn" onClick={confirmDelete}>Delete</button>
          </div>
        }
      >
        <div className="p-4">
          <p>Are you sure you want to delete <strong>"{cardToDelete?.title}"</strong>? This action cannot be undone.</p>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={showMetricLibraryModal}
        onClose={() => setShowMetricLibraryModal(false)}
        title="Metric Library"
        customWidth="1200px"
      >
        <div className="metric-library-wrapper">
          <MetricCardLibrary 
            onClose={() => {
              setShowMetricLibraryModal(false);
              loadCards();
            }} 
          />
        </div>
      </SharedModal>
    </div>
  );
};

export default BiDashboard;
